import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as Yup from 'yup';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { Form, Formik } from 'formik';
import { Box, Divider, Grid, IconButton, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import moment from 'moment';

import useNotification from '../../../../../../core/hooks/use_notification';
import { Button, SubmitButton } from '../../../../../common/button';
import { getProductsAvailabilitySingleDayNotificationData, importProductAvailabilityNotificationData, } from '../../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../../core/enums/common/api';
import { MISC_DATE_ISO_DATE_ONLY_FORMAT } from '../../../../../../core/constants';
import { getProductsAvailabilitySingleDayAction, getProductsAvailabilitySingleDayState } from '../../../../../../features/reservation/get_products_availability_single_day';
import { importProductAvailabilityAction, importProductAvailabilityState } from '../../../../../../features/reservation/import_product_availability';
import DatePickerFormik from '../../../../../common/date_picker';
import { parseDateString } from '../../../../../../core/utils/date_utils';

const initialQueryParams = {
    languageCode: 'PL'
};

const ImportProductAvailabilityFormDialog = ({ refreshCalendar, targetDate }) => {
    const { showNotification, showErrorMessage } = useNotification();
    const dispatch = useDispatch();

    const getProductsAvailabilitySingleDay = useSelector(getProductsAvailabilitySingleDayState);
    const importProductAvailability = useSelector(importProductAvailabilityState);

    const [open, setOpen] = useState(false);

    const onClose = () => setOpen(false);

    const onOpen = () => {
        onChangeDate(initialValues.sourceDate);
        setOpen(true);
    };

    const initialValues = {
        sourceDate: moment(targetDate).subtract(1, 'days')
    };

    const schema = Yup.object().shape({
        sourceDate: Yup.date()
            .transform(parseDateString)
            .nullable()
            .required()
    });

    const onSubmit = (values) => {
        const queryParams = {
            ...initialQueryParams,
            sourceDate: values?.sourceDate?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT),
            targetDate
        };

        dispatch(importProductAvailabilityAction(queryParams))
            .then(response => {
                showNotification(importProductAvailabilityNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    onClose();
                    refreshCalendar && refreshCalendar();
                }
            });
    };

    const onChangeDate = (date) => {
        const queryParams = {
            date: date?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT)
        };

        dispatch(getProductsAvailabilitySingleDayAction(queryParams))
            .then(response => {
                showErrorMessage(getProductsAvailabilitySingleDayNotificationData(response));
            });
    };

    return (
        <>
            <Tooltip title="Zaimportuj listę produktów">
                <IconButton size="small" onClick={ onOpen }>
                    <FileDownloadIcon fontSize="small"/>
                </IconButton>
            </Tooltip>

            <Dialog disableRestoreFocus open={ open } onClose={ onClose } fullWidth maxWidth="md">
                <DialogTitle sx={ { fontWeight: 600, pb: 1 } }>Zaimportuj listę produktów ({ targetDate })</DialogTitle>
                <Divider/>

                <DialogContent sx={ { py: 3 } }>
                    <Formik
                        initialValues={ initialValues }
                        validationSchema={ schema }
                        validateOnChange={ false }
                        validateOnBlur={ false }
                        onSubmit={ onSubmit }>
                        { () => (
                            <Form>
                                <Grid container spacing={ 3 }>
                                    <Grid item xs={ 12 } sm={ 6 }>
                                        <DatePickerFormik
                                            name="sourceDate"
                                            label="Data źródłowa"
                                            required
                                            fullWidth
                                            onChange={ value => onChangeDate(value) }
                                        />
                                    </Grid>
                                </Grid>

                                <Box sx={ { mt: 3 } }>
                                    { getProductsAvailabilitySingleDay?.loading && <LinearProgress/> }
                                    { getProductsAvailabilitySingleDay?.data && (
                                        <TableContainer component={ Paper } variant="outlined"
                                                        sx={ { maxHeight: 400 } }>
                                            <Table stickyHeader size="small">
                                                <TableHead>
                                                    <TableRow sx={ {
                                                        '& th': {
                                                            backgroundColor: 'slate.50',
                                                            fontWeight: 600
                                                        }
                                                    } }>
                                                        <TableCell>Znalezione Produkty</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    { getProductsAvailabilitySingleDay?.data?.map(product => (
                                                        <TableRow key={ product?.productId } hover>
                                                            <TableCell>{ product?.productName }</TableCell>
                                                        </TableRow>
                                                    )) }
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) }
                                </Box>

                                <Divider sx={ { mt: 3, mb: 2, mx: -3 } }/>

                                <DialogActions sx={ { p: 0, gap: 1 } }>
                                    <Button variant="outlined" onClick={ onClose }>Zamknij</Button>
                                    <SubmitButton isLoading={ importProductAvailability?.loading }>Wczytaj
                                        listę</SubmitButton>
                                </DialogActions>
                            </Form>
                        ) }
                    </Formik>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ImportProductAvailabilityFormDialog;